import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CommonPieChart from "../../CommonComponents/CommonPieChart";
import FecthingPage from "../../CommonComponents/FecthingPage";
import GraphHeader from "../../CommonComponents/GraphHeader";
import {
  beneTransactionCount,
  fieldForceTransactionCount,
  internalTransactionCount,
  reimbursementTransactionCount,
  resetError,
} from "../../../../../redux/features/Settings/Statistics";
import {
  errorcode,
  ScreenSizeConfiguration,
} from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";

// NOTE: This is default dummy data for pie chart - used as fallback before fetch
const DEFAULT_GRAPH_DATA = {
  seriesData: [
    {
      label: "Unsuccessful Transactions",
      value: 30,
      color: "var(--negative-act)",
    },
    {
      label: "Successful Transactions",
      value: 70,
      color: "var(--positive-act)",
    },
    {
      label: "Processing",
      value: 10,
      color: "var(--queued)",
    },
    {
      label: "Require approval",
      value: 20,
      color: "var(--pending)",
    },
  ],
  totalTransactions: 130,
  successfulTransactions: 70,
  unSuccessfulTransactions: 30,
  processingTransactions: 10,
  requireApprovalTransactions: 20,
};

/**
 * TransactionCounts - A component that displays transaction statistics in a pie chart
 * with detailed breakdown cards. Supports multiple transaction types (internal, beneficiary,
 * field force, reimbursement) and time period filtering.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isAddedToHome - Whether this widget is added to home dashboard
 * @param {Function} props.toggleAddedToHome - Callback to toggle widget on home dashboard
 * @param {boolean} [props.autoFetch=false] - Whether to fetch data automatically on mount
 * @param {string} props.flow - Transaction flow type: 'internal', 'bene', 'fieldForce', 'reimbursement', or 'homeDashboard'
 * @returns {JSX.Element} Rendered transaction statistics component
 */
const TransactionCounts = ({
  isAddedToHome,
  toggleAddedToHome,
  autoFetch = false,
  flow,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginData = useSelector((state) => state.refreshToken.data);
  const services = useSelector((state) => state.refreshToken.services);
  const globalStatisticsService = services?.includes("VStatistics"); // Global service for statistics.

  const {
    internalTransactionCountLoading,
    beneTransactionCountLoading,
    beneTransactionCountError,
    internalTransactionCountError,
    fieldForceTransactionCountError,
    reimbursementTransactionCountError,
    fieldForceTransactionCountLoading,
    reimbursementTransactionCountLoading,
  } = useSelector((state) => state.settingStatistics);

  // Extract entity ID from login data if user is authenticated
  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  // Flows that are displayed on the dashboard (used for conditional styling)
  const DASHBOARD_FLOWS = ["internal", "bene", "fieldForce"];

  const toastShownRef = useRef(false);

  // Track mobile viewport for responsive chart rendering
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= ScreenSizeConfiguration.mobileScreen,
  );

  // Graph data state - initialized with default dummy data
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);

  // Time period filter state (defaults to last 7 days)
  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );

  // Transaction type filter state - initialized based on flow prop
  const [selectedTxnOption, setSelectedTxnOption] = useState(
    flow === "internal"
      ? "internal"
      : flow === "fieldForce"
        ? "fieldForce"
        : flow === "reimbursement"
          ? "reimbursement"
          : "bene",
  );

  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);

  // Loading state for manual fetch button
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);

  // State to track when no data is available from API
  const [noData, setNoData] = useState(false);

  // Map API response to graph data
  const mapReportToGraphData = (reportData, type) => {
    // Select the correct nested data key based on transaction type
    const dataKey =
      type === "bene"
        ? reportData.beneficiary_transaction_count
        : type === "fieldForce"
          ? reportData.field_force_transaction_count
          : type === "reimbursement"
            ? reportData.reimbursement_transaction_count
            : reportData.internal_transaction_count;
    return {
      seriesData: [
        {
          label: "Unsuccessful Transactions",
          value: dataKey?.unsuccessful_transaction,
          color: "var(--negative-act)",
        },
        {
          label: "Successful Transactions",
          value: dataKey?.successful_transaction,
          color: "var(--positive-act)",
        },
        {
          label: "Processing",
          value: dataKey?.in_progress_transaction,
          color: "var(--queued)",
        },
        {
          label: "Require approval",
          value: dataKey?.pending_transaction,
          color: "var(--pending)",
        },
      ],
      totalTransactions: dataKey?.total_transaction,
      successfulTransactions: dataKey?.successful_transaction,
      unSuccessfulTransactions: dataKey?.unsuccessful_transaction,
      processingTransactions: dataKey?.in_progress_transaction,
      requireApprovalTransactions: dataKey?.pending_transaction,
    };
  };

  /**
   * Processes API response and updates component state accordingly.
   * On success: maps data, clears error, disables fetch page.
   * On failure: resets to default data, shows error state, conditionally enables fetch page.
   */
  const handleCalculation = useCallback(
    (response, type) => {
      const reportData = response?.payload?.report_data;
      const status = response?.payload?.status;

      if (status && reportData) {
        // Successful response - map and display data
        const mappedData = mapReportToGraphData(reportData, type);
        setGraphData(mappedData);
        setIsError(false);
        setNoData(false);

        setFetchPageEnabled(false);
        toastShownRef.current = true;
      } else {
        // Failed response - fallback to default data
        setGraphData(DEFAULT_GRAPH_DATA);
        // Enable fetch page unless it's a "not found" error (which means no data exists)
        setFetchPageEnabled(
          response?.payload?.status !== errorcode?.errorNotFound,
        );
        toastShownRef.current = false;
        setIsError(true);
      }
    },
    [selectedDays, entityId],
  );

  /**
   * Fetches transaction count data from the API based on selected filters.
   * Dispatches the appropriate Redux thunk based on transaction type.
   */
  const fetchData = async () => {
    // Guard: no entity ID
    if (!entityId) return;

    // Clear any previous errors before new fetch
    dispatch(resetError());

    // Guard: user lacks statistics service permission
    if (!globalStatisticsService) {
      setFetchPagefetchPageLoading(false);
      return;
    }

    // Dispatch appropriate thunk based on selected transaction type
    const response = await dispatch(
      selectedTxnOption === "bene"
        ? beneTransactionCount({ entity_id: entityId, period: selectedDays })
        : selectedTxnOption === "fieldForce"
          ? fieldForceTransactionCount({
              entity_id: entityId,
              period: selectedDays,
            })
          : selectedTxnOption === "reimbursement"
            ? reimbursementTransactionCount({
                entity_id: entityId,
                period: selectedDays,
              })
            : internalTransactionCount({
                entity_id: entityId,
                period: selectedDays,
              }),
    );
    setFetchPagefetchPageLoading(false);
    handleCalculation(response, selectedTxnOption);
  };

  /**
   * Effect: Trigger data fetch when autoFetch is enabled or fetch page is disabled.
   * Also cleans up Redux errors on unmount.
   */
  useEffect(() => {
    if (autoFetch || !fetchPageEnabled) {
      fetchData();
    }

    return () => {
      dispatch(resetError());
    };
  }, [autoFetch, fetchPageEnabled, selectedDays, selectedTxnOption]);

  /**
   * Effect: Track window resize for responsive mobile detection.
   * Updates isMobile state when viewport crosses mobile breakpoint.
   */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= ScreenSizeConfiguration.mobileScreen);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * Handler for manual "Fetch Data" button click.
   * Navigates to feature-not-assigned page if user lacks statistics service.
   * Otherwise initiates data fetch.
   */
  const handleFetchClick = () => {
    if (!globalStatisticsService) {
      navigate("/feature_not_assigned", {
        state: { unavailableService: "VStatistics" },
      });
    }
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${flow === "homeDashboard" ? "rounded-b-border-radius-md" : "rounded-border-radius-md"} ${noData ? "h-graphic-square-height-lg" : ""}`}
    >
      {/* Header with title, period filter, transaction type filter, and add-to-home toggle */}
      <GraphHeader
        handlingOptionsRequired={[
          { type: "days", setterState: setSelectedDays },
          { type: "txnOption", setterState: setSelectedTxnOption },
        ]}
        titleText={noData ? "Statistics" : "Transactions count"}
        autoFetch={autoFetch}
        hideTitle={flow === "homeDashboard" ? true : false}
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
        flow={flow}
      />

      <div className="flex flex-col relative">
        {/* Fetch/Loading/Error state page */}
        <FecthingPage
          autoFetch={autoFetch}
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={
            internalTransactionCountLoading ||
            beneTransactionCountLoading ||
            fieldForceTransactionCountLoading ||
            reimbursementTransactionCountLoading ||
            fetchPageLoading
          }
          errorData={
            internalTransactionCountError ||
            beneTransactionCountError ||
            fieldForceTransactionCountError ||
            reimbursementTransactionCountError
              ? { status: errorcode?.errorNotFound }
              : null
          }
          isError={isError}
          handleFunction={handleFetchClick}
        />

        {/* Pie Chart Visualization */}
        <div className={`w-width-full py-padding-md flex justify-center `}>
          <CommonPieChart
            dataArray={graphData.seriesData}
            isMobile={isMobile}
          />
        </div>

        {/* Statistics Breakdown Cards - Shows individual transaction counts */}
        <div className="w-width-full bg-primary-nav rounded-b-border-radius-md overflow-hidden">
          <div className="flex flex-col Qlg:px-padding-md Qlg:flex-row w-width-full justify-between">
            {/* Total Transactions Card */}
            <div
              className={`flex gap-gap-sm Qlg:gap-gap-xs Qlg:w-width-none Qlg:pr-padding-xs Qlg:pl-padding-base px-padding-md  py-padding-sm justify-between items-center relative z-z-index-content Qlg:rounded-b-border-radius-md Qlg:rounded-bl-border-radius-md Qlg:rounded-br-none flex-shrink ${!globalStatisticsService ? "pointer-events-auto opacity-light-opacity cursor-not-allowed" : ""}`}
            >
              <div className="flex justify-between gap-gap-sm items-center w-full Qlg:w-auto Qlg:gap-gap-sm">
                <div className="hidden Qlg:block flex-shrink text-text-secondary text-font-xs whitespace-nowrap">
                  Total transactions :
                </div>
                <div className="Qlg:hidden text-text-secondary text-font-xs">
                  Total transactions
                </div>
                <div className="text-text-primary text-font-md Qlg:text-font-xs font-semibold">
                  {fetchPageEnabled || !globalStatisticsService
                    ? autoFetch
                      ? "N/A"
                      : "-"
                    : (graphData?.totalTransactions?.toLocaleString("en-US") ??
                      "N/A")}{" "}
                </div>
              </div>
            </div>

            <div className="border-primary-bg border-border Qlg:my-margin-sm Qlg:mx-margin-base mx-margin-md"></div>

            {/* Processing Transactions Card */}
            <div
              className={`flex gap-gap-sm Qlg:gap-gap-xs Qlg:w-width-none Qlg:px-padding-xs px-padding-md py-padding-sm justify-between items-center  relative z-z-index-content  flex-shrink ${!globalStatisticsService ? "pointer-events-auto opacity-light-opacity cursor-not-allowed" : ""}`}
            >
              <div className="flex justify-between items-center w-full Qlg:w-auto Qlg:gap-gap-sm">
                <div className="hidden Qlg:block flex-shrink text-text-secondary text-font-xs whitespace-nowrap">
                  Processing :
                </div>
                <div className="Qlg:hidden text-text-secondary text-font-xs ">
                  Processing
                </div>
                <div className="text-queued text-text-md Qlg:text-font-xs font-semibold">
                  {fetchPageEnabled || !globalStatisticsService
                    ? autoFetch
                      ? "N/A"
                      : "-"
                    : (graphData?.processingTransactions?.toLocaleString(
                        "en-US",
                      ) ?? "N/A")}
                </div>
              </div>
            </div>

            <div className="border-primary-bg border-border Qlg:my-margin-sm Qlg:mx-margin-base mx-margin-md"></div>

            {/* Require Approval Transactions Card */}
            <div
              className={`flex gap-gap-sm Qlg:gap-gap-xs Qlg:w-width-none Qlg:px-padding-xs px-padding-md py-padding-sm justify-between items-center  relative z-z-index-content  flex-shrink ${!globalStatisticsService ? "pointer-events-auto opacity-light-opacity cursor-not-allowed" : ""}`}
            >
              <div className="flex justify-between items-center w-full Qlg:w-auto Qlg:gap-gap-sm">
                <div className="hidden Qlg:block flex-shrink text-text-secondary text-font-xs whitespace-nowrap">
                  Require approval :
                </div>
                <div className="Qlg:hidden text-text-secondary text-font-xs ">
                  Require approval
                </div>
                <div className="text-pending text-text-md Qlg:text-font-xs font-semibold">
                  {fetchPageEnabled || !globalStatisticsService
                    ? autoFetch
                      ? "N/A"
                      : "-"
                    : (graphData?.requireApprovalTransactions?.toLocaleString(
                        "en-US",
                      ) ?? "N/A")}
                </div>
              </div>
            </div>
            <div className="border-primary-bg border-border Qlg:my-margin-sm Qlg:mx-margin-base mx-margin-md"></div>

            {/* Successful Transactions Card */}
            <div
              className={`flex Qlg:w-width-none gap-gap-sm Qlg:gap-gap-xs Qlg:px-padding-xs px-padding-md py-padding-sm justify-between items-center  relative z-z-index-content  Qlg:rounded-b-border-radius-md Qlg:rounded-bl-none Qlg:rounded-br-none flex-shrink ${!globalStatisticsService ? "pointer-events-auto opacity-light-opacity cursor-not-allowed" : ""}`}
            >
              <div className="flex justify-between items-center w-full Qlg:w-auto Qlg:gap-gap-sm">
                <div className="hidden Qlg:block  flex-shrink text-text-secondary text-font-xs whitespace-nowrap">
                  Successful :
                </div>
                <div className="Qlg:hidden text-text-secondary text-font-xs">
                  Successful
                </div>
                <div className="text-positive-act text-text-md Qlg:text-font-xs font-semibold">
                  {fetchPageEnabled || !globalStatisticsService
                    ? autoFetch
                      ? "N/A"
                      : "-"
                    : (graphData?.successfulTransactions?.toLocaleString(
                        "en-US",
                      ) ?? "N/A")}
                </div>
              </div>
            </div>

            <div className="border-primary-bg border-border Qlg:my-margin-sm Qlg:mx-margin-base mx-margin-md"></div>

            {/* Unsuccessful Transactions Card */}
            <div
              className={`flex Qlg:w-width-none gap-gap-sm Qlg:gap-gap-xs Qlg:pr-padding-base Qlg:pl-padding-xs px-padding-md py-padding-sm justify-between items-center  relative z-z-index-content Qlg:rounded-b-border-radius-md Qlg:rounded-bl-none Qlg:rounded-br-none flex-shrink ${!globalStatisticsService ? "pointer-events-auto opacity-light-opacity cursor-not-allowed" : ""}`}
            >
              <div className="flex justify-between items-center w-full Qlg:w-auto Qlg:gap-gap-sm">
                <div className="hidden Qlg:block flex-shrink text-text-secondary text-font-xs whitespace-nowrap">
                  Unsuccessful :
                </div>
                <div className="Qlg:hidden text-text-secondary text-font-xs ">
                  Unsuccessful
                </div>
                <div className="text-error text-text-md Qlg:text-font-xs font-semibold">
                  {fetchPageEnabled || !globalStatisticsService
                    ? autoFetch
                      ? "N/A"
                      : "-"
                    : (graphData?.unSuccessfulTransactions?.toLocaleString(
                        "en-US",
                      ) ?? "N/A")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionCounts;
